import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnInit,
  ViewChild,
  HostListener,
  Output,
  EventEmitter
} from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs';

import { PieceProductSettings } from 'extensions/core/piece-product/front/models/piece-product.models';
import { ImageDetails } from 'extensions/core/piece-product/front/piece-product-render.component';
import { ImageLocation } from 'extensions/core/piece-product-carousel/front/models/piece-product-carousel.model';
import { ProductsHelpers } from 'idt-ecommerce-api/catalog/products/products.helpers';
import { Product, ProductPartNumberDetails, ProductSize } from 'idt-ecommerce-api/catalog/products/products.models';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { CMSEvent, CMSSelectedInternationalSizeEvent, CMSSelectedProductSizeEvent } from 'render/models/cms-event.model';
import * as ModalPanelActions from 'render/render-shared/ui/render-modal-panel/store/render-modal-panel.actions';
import { RenderState } from 'render/render.state';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { CMSRenderEventsService } from 'render/services/cms-render-events.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { UnsubscribeComponent } from 'shared/directives/unsubscribe.component';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';
import { InditexWindow } from 'shared/models/inditex-window.models';
import { AssetsSharedService } from 'shared/services/assets-shared.service';

import { RenderSelectComponent } from '../render-select/render-select.component';

import { templateUrl, styleUrls } from './render-product-purchase-options.component.paths';


@Component({
  selector: 'render-product-purchase-options',
  templateUrl,
  styleUrls,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RenderProductPurchaseOptionsComponent extends UnsubscribeComponent implements OnInit {

  @ViewChild('sizeSelectorComponent') public sizeSelector: RenderSelectComponent | undefined;
  @Input() public product!: Product;
  @Input() public imageDetails: ImageDetails | undefined;
  @Input() public piece: IdtComponent<PieceProductSettings> | undefined;
  @Input() public country?: string;
  @Input() public lang?: string;
  @Input() public isModal?: boolean = false;
  @Input() public originInfoEvent?: string;
  @Input() public providerInfoEvent?: string;
  @Input() public strategyInfoEvent?: string;
  @Input() public objectiveInfoEvent?: string;
  @Output() public selectorClosed = new EventEmitter<void>();

  public selectedSize?: string;
  public productSizes: ProductSize[][] = [];
  public productSizesAllBackSoon: boolean[] = [];
  public sizeTypeSelected?: ProductSize[];
  public alternativeSizesNames: string[] = [];
  public hasToShowInternationalSizeName: boolean = false;
  public storeAvailability?: boolean = false;
  public isModalOpen: boolean = false;
  public isNavigatingInside: boolean = false;
  public productImageData?: LazyDataInterface;
  private assetId?: string;

  constructor(
    @Inject('Window') private readonly window: InditexWindow,
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    private readonly renderByDeviceService: RenderByDeviceService,
    protected readonly renderConfigurationService: RenderConfigurationService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly contextService: ContextService,
    protected readonly store: Store<RenderState>,
    private readonly cd: ChangeDetectorRef,
    private readonly elementRef: ElementRef
  ) {
    super();
  }

  @HostListener('keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    if (!this.isModalOpen) {
      switch (event.key) {
        case 'Escape':
          this.closeSizeSelector();
          event.preventDefault();
          break;

        case 'ArrowDown':
        case 'ArrowRight': {
          event.preventDefault();
          this.focusNextElement(event.target as HTMLElement, 'forward');
          break;
        }

        case 'ArrowUp':
        case 'ArrowLeft': {
          event.preventDefault();
          this.focusNextElement(event.target as HTMLElement, 'backward');
          break;
        }
        default:
          break;
      }
    }
  }

  public get hasToDisplayProductSizesSelector(): boolean {
    return !!this.piece?.settings?.general?.displayProductSizesSelector;
  }

  public get hasToDisplayProductSizesAdditionalInfo(): boolean {
    return !!this.piece?.settings?.general?.displayProductSizesAdditionalInfo;
  }

  public get hasToDisplayProductSizesLastUnitsWarning(): boolean {
    return !!this.piece?.settings?.general?.displayLastSizeUnitsWarning;
  }

  public get hasToDisplayViewSimilarProducts(): boolean {
    return !!this.piece?.settings?.general?.displayViewSimilar;
  }

  public get isMobile(): boolean {
    return this.renderByDeviceService.isMobile;
  }

  public ngOnInit(): void {
    this.contextService.getContext$().pipe(
      distinctUntilChanged(),
      filter((context: EcommerceContext) => !!context?.store?.details?.storeAvailability),
      takeUntil(this.clearSubscriptions$)
    ).subscribe((context: EcommerceContext) => {
      this.storeAvailability = context.store.details.storeAvailability;
    });


    const defaultSizes = this.product?.colors[0]?.sizes || [];
    const sizes = this.product?.colors.find(
      (color) => color.id === this.imageDetails?.colorId
    )?.sizes || defaultSizes;
    const groupedSizes: { [key: string]: ProductSize[] } = {};
    const orderedKeys: string[] = [];

    for (const productSize of sizes) {
      const key = productSize.name!;
      if (!groupedSizes[key]) {
        groupedSizes[key] = [];
        orderedKeys.push(key);
      }
      groupedSizes[key].push(productSize);
    }
    this.productSizes = orderedKeys.map((key) => groupedSizes[key]);
    this.productSizesAllBackSoon = this.productSizes.map((sizeGroup) => this.hasBackSoonForAllSizes(sizeGroup));

    // In this case, we only check the first element of the sizes array because if one size has the
    // internationalSizeName, the rest of the product sizes must have it.
    if (sizes[0]?.internationalSizeName) {
      this.alternativeSizesNames = this.getAlternativeSizesNames();
    }
    this.productImageData = this.getProductImageData(this.product);
  }

  public onProductCartClick(size?: string): void {
    if (this.hasToDisplayProductSizesSelector && !this.selectedSize && !size) {
      this.sizeSelector?.toggle();
    } else {
      if (size && !this.sizeTypeSelected) {
        this.onSizeSelected(size);
      }
      if (size && this.sizeTypeSelected) {
        this.onSizeSelectedLength(size);
      }

      const cmsEvent: CMSEvent<Product> = {
        name: 'productCartClicked',
        type: 'click',
        identifier: 'add_to_cart',
        data: {
          ...this.product,
          ...this.getProductPartsFromDetails()
        },
        ...this.originInfoEvent && { origin: this.originInfoEvent },
        ...this.providerInfoEvent && { provider: this.providerInfoEvent },
        ...this.strategyInfoEvent && { strategy: this.strategyInfoEvent },
        ...this.objectiveInfoEvent && { objective: this.objectiveInfoEvent },
        ...this.assetId && { assetId: this.assetId }
      };
      this.cmsRenderEventsService.emitEvent(cmsEvent);

      setTimeout(() => {
        if (this.isMobile) {
          this.store.dispatch(ModalPanelActions.closeModal({ modalName: `size-selector-modal-${this.piece!.id}` }));
          this.cd.markForCheck();
        }

        setTimeout(() => {
          this.showAddToCartMessage();
        }, 500);
      }, 1000);
    }
  }

  public onSizeSelected(size: string): void {
    this.selectedSize = size;

    const cmsEvent: CMSEvent<CMSSelectedProductSizeEvent> = {
      name: 'productSizeSelected',
      type: 'click',
      origin: this.originInfoEvent,
      data: {
        product: this.product,
        size,
        name: 'select_content',
        contentType: 'product_size',
        actionType: 'seleccionar_talla',
        linkType: 'texto',
        eventData: 'catalogo',
        ...this.getProductPartsFromDetails()
      }
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);
  }

  public onSizeSelectedLength(size: string): void {
    this.selectedSize = size;

    const cmsEvent: CMSEvent<CMSSelectedProductSizeEvent> = {
      name: 'productSizeLengthSelected',
      type: 'click',
      origin: this.originInfoEvent,
      data: {
        product: this.product,
        size,
        name: 'select_content',
        contentType: 'product_size',
        actionType: 'seleccionar_largo',
        linkType: 'texto',
        eventData: 'catalogo',
        ...this.getProductPartsFromDetails()
      }
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);
  }

  public onViewSimilarClick(size: string): void {
    const cmsEvent: CMSEvent<CMSSelectedProductSizeEvent> = {
      name: 'viewSimilarProductsClicked',
      type: 'click',
      data: {
        product: this.product,
        size,
        contentType: 'product_info',
        actionType: 'product_info',
        ...this.getProductPartsFromDetails()
      }
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);
  }

  public openSizeSelectorModal(): void {
    if (this.product.colors[0]?.sizes && this.product.colors[0].sizes.length === 1) {
      this.onProductCartClick(JSON.stringify(this.product.colors[0].sizes[0]));
    } else {
      document.body.style.overflowY = 'hidden';
      this.store.dispatch(ModalPanelActions.openModal(
        {
          modalName: `size-selector-modal-${this.piece!.id}`,
          props: {
            data: {
              product: this.product,
              imageDetails: this.imageDetails,
              piece: this.piece,
              country: this.country,
              lang: this.lang
            }
          }
        }
      ));
    }
  }

  public showSizeType(sizes: ProductSize[]): void {
    const sizeOrder = ['short', 'regular', 'long'];
    const sortSizes: Record<string, number> = sizeOrder
      .reduce((obj, item, index) => ({
        ...obj,
        [item]: index
      }), {});
    this.sizeTypeSelected = [...sizes].sort((a, b) => sortSizes[a.sizeType!] - sortSizes[b.sizeType!]);
  }

  public removeSizeType(): void {
    const focusToMainSelector = (): void => {
      const sizeContainer = (this.elementRef.nativeElement as HTMLElement).querySelector('.product-size-container');
      if (sizeContainer) {
        const firstSizeButton = sizeContainer.querySelector('.product-size-button:not([disabled])') as HTMLElement;
        if (firstSizeButton) {
          this.setupSizeSelectorNavigation();
          firstSizeButton.focus();
        }
      }
    };

    this.sizeTypeSelected = undefined;
    this.selectedSize = undefined;

    setTimeout(() => {
      focusToMainSelector();
      this.cd.markForCheck();
    }, 50);
  }

  public handleBackButtonEnter(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.removeSizeType();
  }

  public trackBySize(index: number, size: ProductSize): string {
    return size.sku;
  }

  public toggleInternationalSizeName(): void {
    const cmsEvent: CMSEvent<CMSSelectedInternationalSizeEvent> = {
      name: 'selectInternationalSizeName',
      type: 'click',
      origin: this.originInfoEvent,
      data: {
        product: this.product,
        name: 'select_content',
        contentType: 'product_size',
        actionType: 'cambiar_formato_tallaje',
        eventData: 'catalogo',
        detail: this.hasToShowInternationalSizeName ? this.alternativeSizesNames[0] : this.alternativeSizesNames[1],
        ...this.getProductPartsFromDetails()
      }
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);
    this.hasToShowInternationalSizeName = !this.hasToShowInternationalSizeName;
  }

  public hasBackSoonForAllSizes(sizes: ProductSize[]): boolean {
    return sizes.every((size) => size.backSoon === '1');
  }

  public onBackSoonClick(sizes: ProductSize[]): void {
    const cmsEvent: CMSEvent<CMSSelectedProductSizeEvent> = {
      name: 'select_content',
      type: 'click',
      origin: this.originInfoEvent,
      data: {
        product: this.product,
        size: sizes[0],
        contentType: 'product_size',
        actionType: 'avisame',
        ...this.getProductPartsFromDetails()
      }
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);

    this.window.openNotifyModalComponent?.({
      isMobile: this.isMobile,
      allProductsBackSoon: this.hasBackSoonForAllSizes(sizes),
      productName: this.product.name,
      productId: this.product.id,
      image: this.productImageData?.src,
      sku: sizes[0].sku,
      sizes: this.productSizes.map((size) => size[0]),
      isUniqueSize: this.productSizes.length === 1,
      hasSizeTypes: sizes.length > 1,
      sizeTypes: sizes,
      selectedSize: sizes[0],
      productColorRef: this.product.productReference,
      storeAvailability: this.storeAvailability,
      closeModalAdapter: () => {
        document.documentElement.style.overflowY = 'auto';
      }
    });
  }

  public getProductImageData(product: Product): LazyDataInterface | undefined {
    const width = 600;
    const location: ImageLocation = 'category_page';

    const media = ProductsHelpers.firstImageForFirstColor(product?.colors, location, width);
    this.assetId = media?.extraInfo?.assetId;
    const imageData = this.assetsSharedService.productImageData(
      media,
      width,
      this.renderConfigurationService.config
    );

    return imageData;
  }

  public handleEnterKey(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.openSizeSelectorModal();

    setTimeout(() => {
      const modal = document.querySelector(`[modalname="size-selector-modal-${this.piece?.id ?? ''}"]`);
      if (modal) {
        const firstButton = modal.querySelector('button') as HTMLElement;
        if (firstButton) {
          firstButton.focus();
        }
      }
    }, 100);
  }

  public handleSizeButtonEnter(event: Event, sizes: ProductSize[]): void {
    event.preventDefault();
    event.stopPropagation();

    if (sizes.length === 1) {
      if (this.isMobile) {
        this.onSizeSelected(String(sizes[0])); // eslint-disable-line @typescript-eslint/no-base-to-string
      } else {
        this.onProductCartClick(String(sizes[0])); // eslint-disable-line @typescript-eslint/no-base-to-string
      }
    } else {
      this.showSizeType(sizes);
    }

    const currentButton = event.target as HTMLElement;

    if (sizes.length > 1) {
      setTimeout(() => {
        const typeButtons = (this.elementRef.nativeElement as HTMLElement).querySelectorAll('.product-size-button');
        if (typeButtons.length > 0) {
          this.setupSizeSelectorNavigation();
          (typeButtons[0] as HTMLElement).focus();
        }
      }, 100);
    } else {
      currentButton.focus();
    }
  }

  private getProductPartsFromDetails(): ProductPartNumberDetails | undefined {
    if (this.imageDetails?.colorId || this.imageDetails?.style) {
      return {
        ...(this.imageDetails?.colorId && { colorId: this.imageDetails.colorId }),
        ...(this.imageDetails?.style && { style: this.imageDetails.style })
      };
    }

    return undefined;
  }

  private getAlternativeSizesNames(): string[] {
    const sizeNamesByCountry: Record<string, Record<string, string[]>> = {
      US: {
        mobile: ['US SIZE', 'WAIST (In)'],
        desktop: ['US', 'WAIST (In)']
      },
      IT: {
        mobile: this.lang === 'en' ? ['IT SIZE', 'EU SIZE'] : ['TAGLIA IT', 'TAGLIA EUR'],
        desktop: ['IT', 'EU']
      },
      MX: {
        mobile: this.lang === 'en' ? ['MEX SIZE', 'US SIZE'] : ['TALLA MEX', 'TALLA US'],
        desktop: ['MEX', 'US']
      },
      CA: {
        mobile: this.lang === 'en' ? ['WAIST (In)', 'CA SIZE'] : ['TAILLE', 'TAILLE CA'],
        desktop: ['WAIST (In)', 'CA']
      }
    };

    const countrySizes = sizeNamesByCountry[this.country?.toUpperCase() ?? ''];
    if (countrySizes) {
      return this.isMobile ? countrySizes.mobile : countrySizes.desktop;
    }

    return [];
  }

  private focusNextElement(currentButton: HTMLElement, direction: 'forward' | 'backward'): void {
    const buttons: HTMLElement[] = Array.from(
      (this.elementRef.nativeElement as HTMLElement).querySelectorAll('.product-size-button:not([disabled])')
    );

    const currentIndex = buttons.indexOf(currentButton);
    if (currentIndex !== -1 && buttons.length > 0) {
      const index = direction === 'forward' ? (currentIndex + 1) % buttons.length : (currentIndex - 1 + buttons.length) % buttons.length;
      buttons[index].focus();
    }
  }

  private setupSizeSelectorNavigation(): void {
    const sizeContainer = (this.elementRef.nativeElement as HTMLElement).querySelector('.product-size-container');
    if (sizeContainer) {
      const interactiveElements: HTMLElement[] = Array.from(sizeContainer.querySelectorAll(
        'button:not([disabled]), select:not([disabled]), a:not([disabled])'
      ));

      if (interactiveElements.length === 0) {
        return;
      }

      interactiveElements.forEach((el) => {
        el.setAttribute('tabindex', '0');
      });
    }
  }

  private closeSizeSelector(): void {
    (this.elementRef.nativeElement as HTMLElement).style.display = 'none';
    this.selectorClosed.emit();
    this.announceToScreenReader('Selector de tallas cerrado');
  }

  private announceToScreenReader(message: string): void {
    const liveRegion = document.createElement('div');
    liveRegion.className = 'sr-only';
    liveRegion.setAttribute('aria-live', 'assertive');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.textContent = message;

    document.body.appendChild(liveRegion);
    setTimeout(() => document.body.removeChild(liveRegion), 1000);
  }

  private showAddToCartMessage(): void {
    this.store.dispatch(ModalPanelActions.openModal(
      {
        modalName: `add-to-cart-modal-${this.piece!.id}`,
        props: {
          data: {
            product: this.product,
            size: this.selectedSize
          }
        }
      }
    ));

    setTimeout(() => {
      this.store.dispatch(ModalPanelActions.closeModal({ modalName: `add-to-cart-modal-${this.piece!.id}` }));
      this.removeSizeType();
    }, 4000);
  }

}
